<script setup>
import { computed, reactive, ref, watch } from "vue";
import { storeCurrentSession } from "@/utils/cognito";

import { useRoute, useRouter } from "vue-router";

import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, maxLength, minValue, maxValue, numeric, sameAs, helpers } from '@vuelidate/validators'

import useAlert from "@/hooks/alert";
import WawLabel from "@/components/ui/Label.vue";
import WawButton from "@/components/ui/Button.vue";
import { XCircleIcon, CheckCircleIcon } from '@heroicons/vue/24/solid';
import { useQuery, provideApolloClient, useMutation } from '@vue/apollo-composable'
import { InformationCircleIcon } from "@heroicons/vue/24/outline";
import * as Sentry from "@sentry/vue";
import { useDateOfBirthVerificationStatusStore } from '@/stores/dateOfBirthVerificationStatus';
import { gqlVerifyBirthdate, gqlGetClientBirthdate } from "@/graphql/clients";
import { apolloClient } from "@/utils/apolloClient";
import {APP} from "@/config/app";
import Button from "@Atoms/Button.vue";
import AtomButton from "@Atoms/Button.vue";

provideApolloClient(apolloClient);

let loading = ref(false);

// creates a reference to Vue router
const router = useRouter();
const route = useRoute();

//sets up hook for message alerting
const { message, messageStyleType, setMessage } = useAlert();

// set up DoB form variables
const dobForm = reactive({
  day: '',
  month: '',
  year: '',
});

const errors = ref("");
const verifying = ref(false);

const dobFormRules = computed(() => ({
  day: {
    required,
    maxLength: maxLength(2),
    minValue: minValue(1),
    maxValue: maxValue(31),
    numeric,
  },
  month: {
    required,
    maxLength: maxLength(2),
    minValue: minValue(1),
    maxValue: maxValue(12),
    numeric,
  },
  year: {
    required,
    maxLength: maxLength(4),
    minValue: minValue((new Date(1900))),
    maxValue: maxValue((new Date().getFullYear())),
    numeric,
  },
}));

const v4$ = useVuelidate(dobFormRules, dobForm);

const {
  mutate: createDoBVerificationMutation,
  loading: dobVerificationLoading,
  error: dobVerificationError
} = useMutation(gqlVerifyBirthdate);

//const verifyDoB = async () => {
async function verifyDoB() {

  verifying.value = true;
  errors.value = "";
  const validationResult = await v4$.value.$validate();
  if(!validationResult){
    errors.value = "Please enter a valid date of birth";
    verifying.value = false;
    return;
  }

  const birthdate = dobForm.year + '-' + dobForm.month.toString()?.padStart(2, '0') + '-' + dobForm.day.toString()?.padStart(2, '0');
  const verificationResult = await createDoBVerificationMutation({ birthdate });

  if (dobVerificationError.value) {
    errors.value = "We cannot match your date of birth to the information we hold. Please call our service desk on 0800 083 5054 and we will be happy to assist."
    verifying.value = false;
  } else if (verificationResult.data.verifyBirthdate.birthdateVerificationStatus === 'PASSED') {

    // refresh the session to include new permissions added after successful completion of DoB challenge (access the stored birthdate)
    try {
      await storeCurrentSession(true);

      // Get the client birthdate and compare to the input
      const { onResult: onResultBirthdate, loading: loadingBirthdate, error: errorBirthdate  } = useQuery(gqlGetClientBirthdate);

      onResultBirthdate(resultBirthdate => {
        const storeDateOfBirthVerificationStatus = useDateOfBirthVerificationStatusStore();
        const receivedClientBirthdate = resultBirthdate.data?.clientInfo?.birthdate;

        if (!receivedClientBirthdate) {
          return;
        }

        if (verifying.value && receivedClientBirthdate === birthdate) {
          storeDateOfBirthVerificationStatus.verified = true;
          router.replace({
            name: "Dashboard",
          });

        } else {
          errors.value = "We cannot match your date of birth to the information we hold. Please call our service desk on 0800 083 5054 and we will be happy to assist."
        }
        verifying.value = false;

      });

    } catch (error){
      Sentry.captureException(error);
    }
  }
}

</script>

<template>
    <div class="auth-padding">
        <base-message :type="messageStyleType" v-if="message" class="sm:mx-auto sm:w-full sm:max-w-lg">{{ message }}</base-message>

        <div class="text-center 2xl:mb-10 mb-4">
            <h1>Confirm your Date of Birth</h1>
            <div class="std-text">
                Please enter your details below
            </div>
        </div>

        <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-lg">
          <form @submit.prevent="verifyDoB" class="space-y-6">
            <div class="flex flex-row w-full gap-4">
              <div class="flex flex-col flex-grow">
                <WawLabel for="dob-day">Day</WawLabel>
                <input
                    name="dob-day"
                    id="dob-day"
                    type="number"
                    :class="[APP.MyWealthMode ? 'text-input' : 'login-form-input body-3-reg']"
                    placeholder="DD"
                    v-model="dobForm.day"
               min="1" max="31" />
              </div>
              <div class="flex flex-col flex-grow">
                <WawLabel for="dob-month">Month</WawLabel>
                <input
                    name="dob-month"
                    id="dob-month"
                    type="number"
                    :class="[APP.MyWealthMode ? 'text-input' : 'login-form-input body-3-reg']"
                    placeholder="MM"
                    v-model="dobForm.month"
               min="1" max="12" />
              </div>
              <div class="flex flex-col flex-grow">
                <WawLabel for="dob-year">Year</WawLabel>
                <input
                    name="dob-year"
                    id="dob-year"
                    type="number"
                    :class="[APP.MyWealthMode ? 'text-input' : 'login-form-input body-3-reg']"
                    placeholder="YYYY"
                    v-model="dobForm.year"
               :min="new Date().getFullYear() - 200" :max="new Date().getFullYear()" />
              </div>
            </div>

            <Button
                type="submit"
                :isLoading="verifying"
                :disabled="verifying"
                class="w-full mt-8"
                aria-label="Confirm"
                button-type="unset"
                :class="[APP.MyWealthMode ? 'primary-mwp' : 'primary-mwi', 'h-full button-padding body-3-semi-bold rounded-full max-w-max']"
            >
              Confirm
            </Button>

            <div class="h-16 min-h-16 mt-8 flex flex-row text-red-500 text-md">
              <span v-if="errors">
                <InformationCircleIcon class="w-6 h-6 mr-1 inline" />
              </span>
              <span v-if="errors" class="text-red-500 text-md">
                {{ errors }}
              </span>
            </div>
          </form>
        </div>
    </div>
</template>
